/**
 * TransformDefinitions — the shared TRANSFORM system specification.
 *
 * A TRANSFORM lets a player upgrade one minion into ANOTHER class: the
 * original minion is removed and replaced with a fresh level-1 minion of
 * the target class (same battlefield position). Transforms are offered as
 * an extra upgrade option — a "portal" in the upgrade panel — and are
 * offered ONLY at the EXACT level given in the transform spec (a minion at
 * any other level never sees the option; the server re-validates the gate
 * on the upgrade request, so a spoofed request is rejected).
 *
 * A transform spec is:
 *  - `sourceClasses`: the minion classes the transform is offered on,
 *  - `level`: the exact level (1-based) at which the transform is offered,
 *  - `targetClass`: the class the minion becomes (it starts at level 1
 *    with the target class's base stats and base abilities).
 *
 * Shared by EVERY site: the server's upgrade-options builder and upgrade
 * request handler (authoritative), the client's upgrade-panel mirror
 * (UpgradeChoiceUI local fallback + render data), and the tests. APPEND
 * new transforms below — nothing else to wire.
 *
 * NOTE: the current transforms target the LOCKED classes (Blightcaster /
 * Buffer / Runekeeper / Spellmaker — see testMinions.ts), which have no
 * draw path. That is the whole point of the system: a transform is (so far)
 * the only way those classes reach the board.
 */

import { MinionType } from './MinionType';
import { MinionDefinition } from './MinionDefinition';
import {
  BLIGHTCASTER_MINION_DEFINITION,
  BUFFER_MINION_DEFINITION,
  RUNEKEEPER_MINION_DEFINITION,
  SPELLMAKER_MINION_DEFINITION,
} from './testMinions';

/**
 * A single transform specification.
 */
export interface TransformDefinition {
  /** Stable unique identifier (e.g. 'to_runekeeper'). */
  id: string;
  /** The minion classes this transform is offered on (the minion's class). */
  sourceClasses: readonly MinionType[];
  /** The EXACT level (1-based) at which the transform is offered. */
  level: number;
  /** The class the minion becomes (it starts at level 1). */
  targetClass: MinionType;
}

/**
 * The transform catalogue. Single source of truth — APPEND new transforms
 * here.
 */
export const TRANSFORM_DEFINITIONS: readonly TransformDefinition[] = [
  {
    id: 'to_runekeeper',
    sourceClasses: ['warrior', 'tank'],
    level: 3,
    targetClass: 'runekeeper',
  },
  {
    id: 'to_buffer',
    sourceClasses: ['bard'],
    level: 3,
    targetClass: 'buffer',
  },
  {
    id: 'to_blightcaster',
    sourceClasses: ['mage'],
    level: 3,
    targetClass: 'blightcaster',
  },
  {
    id: 'to_spellmaker',
    sourceClasses: ['healer'],
    level: 3,
    targetClass: 'spellmaker',
  },
];

/**
 * The transform offered to a minion of the given class at the given level,
 * or null when no spec matches (wrong class, or the minion is not at the
 * spec's EXACT level — level 5 of a level-4 transform is NOT an offer).
 */
export function getTransformFor(
  minionType: string,
  level: number
): TransformDefinition | null {
  return (
    TRANSFORM_DEFINITIONS.find(
      (t) =>
        t.level === level &&
        t.sourceClasses.includes(minionType as MinionType)
    ) ?? null
  );
}

/**
 * The target classes that have a replacement card definition (the locked
 * trio). A transform whose target class has no definition cannot be
 * applied — getTransformTargetDefinition returns null and the server
 * rejects it.
 */
const TRANSFORM_TARGET_DEFINITIONS: Readonly<Record<string, MinionDefinition>> = {
  blightcaster: BLIGHTCASTER_MINION_DEFINITION,
  buffer: BUFFER_MINION_DEFINITION,
  runekeeper: RUNEKEEPER_MINION_DEFINITION,
  spellmaker: SPELLMAKER_MINION_DEFINITION,
};

/**
 * The replacement MinionDefinition for a transform target class (its base
 * stats + base abilities are what the fresh level-1 minion gets).
 * @param targetClass The target class ID (e.g. 'runekeeper').
 * @returns The definition, or null when the class has no transform target.
 */
export function getTransformTargetDefinition(
  targetClass: string
): MinionDefinition | null {
  return TRANSFORM_TARGET_DEFINITIONS[targetClass] ?? null;
}
